/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { User } from '../domain/entities';
import { canViewRiderSettings } from '../core/security';
import { Rider, RiderSettings } from '../modules/rider-ops/types';
import { getRepository, isRemoteMode } from '../config/repository';
import { getAuthToken, hasApiStatus } from '../data/ApiModuleRepository';
import { useModuleQuery } from './useModuleQuery';

const defaultSettings: RiderSettings = {
  fileNoStart: 1000,
  fileNoPadding: 4,
  contracts: [],
  cardHeader: 'ClickIt Rider Card',
};

/**
 * Hook for accessing module data (riders, settings, etc.).
 *
 * Riders are loaded via the generic useModuleQuery<Rider> hook, which uses
 * IDataRepository.getAll / save / delete — the same path that works for any
 * module and any backend (localStorage today, remote API tomorrow).
 *
 * Settings continue to use the domain-specific IModuleRepository methods since
 * they are per-org key/value objects rather than an array of typed records.
 *
 * @param _moduleId - Forward-compatible module identifier (e.g. 'rider-ops').
 */
export function useModuleData(_moduleId: string, user?: User | null) {
  // ── Riders — via generic IDataRepository.getAll / save / delete ───────────
  const {
    data:    riders,
    loading: ridersLoading,
    refresh: refreshRiders,
    save:    saveRider,
    remove:  removeRider,
  } = useModuleQuery<Rider>('rider-manager');

  // ── Settings — via domain-specific IModuleRepository methods ─────────────
  const [settings, setSettings] = useState<RiderSettings>(defaultSettings);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    if (isRemoteMode() && (!user || !canViewRiderSettings(user) || !getAuthToken())) {
      setSettings(defaultSettings);
      setSettingsLoading(false);
      return;
    }
    setSettingsLoading(true);
    try {
      const s = await getRepository().getSettings(user);
      setSettings(s);
    } catch (err) {
      if (!hasApiStatus(err, 401, 403)) {
        console.error('[useModuleData] Failed to load rider settings', err);
      }
      setSettings(defaultSettings);
    } finally {
      setSettingsLoading(false);
    }
  }, [user]);

  useEffect(() => { void refreshSettings(); }, [refreshSettings]);

  // ── Combined loading / refresh ────────────────────────────────────────────
  const loading = ridersLoading || settingsLoading;

  const refresh = useCallback(async () => {
    await Promise.all([refreshRiders(), refreshSettings()]);
  }, [refreshRiders, refreshSettings]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const nextAutoNumber = useMemo(() => {
    if (riders.length === 0) return settings.fileNoStart;
    const max = Math.max(...riders.map((r) => r.fileNumber));
    return max + 1;
  }, [riders, settings]);

  // ── Rider write operations (generic IDataRepository path) ─────────────────

  const addRider = async (
    riderData: Omit<Rider, 'id' | 'createdAt' | 'isActive' | 'history'> & { fileNumber?: number }
  ) => {
    const fileNumber = riderData.fileNumber || nextAutoNumber;
    const now = Date.now();
    const newRider: Rider = {
      ...riderData,
      fileNumber,
      id: crypto.randomUUID(),
      isActive: true,
      createdAt: now,
      history: riderData.branchId
        ? [{ branchId: riderData.branchId, startDate: now, endDate: null, daysSpent: null }]
        : [],
    };
    await saveRider(newRider);
    return newRider;
  };

  const updateRider = async (rider: Rider) => {
    await saveRider(rider);
  };

  const deleteRider = async (id: string) => {
    await removeRider(id);
  };

  // ── Settings write ────────────────────────────────────────────────────────

  const updateSettings = async (newSettings: RiderSettings) => {
    await getRepository().saveSettings(newSettings);
    await refreshSettings();
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const checkFileNumberConflict = (fileNumber: number, excludeId?: string) => {
    const conflict = riders.find(r => r.fileNumber === fileNumber && r.id !== excludeId);
    return conflict ? conflict.name : null;
  };

  const toggleRiderStatus = async (id: string, logUser?: Pick<User, 'id' | 'name'>) => {
    const rider = riders.find(r => r.id === id);
    if (rider) {
      const newStatus = !rider.isActive;
      await saveRider({ ...rider, isActive: newStatus });
      if (logUser) {
        await getRepository().saveSystemLog({
          id: crypto.randomUUID(),
          userId: logUser.id,
          userName: logUser.name,
          action: `Set rider "${rider.name}" to ${newStatus ? 'Active' : 'Inactive'}`,
          riderId: rider.id,
          riderName: rider.name,
          timestamp: Date.now(),
          moduleId: 'rider-ops',
        });
      }
    }
  };

  return {
    riders,
    settings,
    loading,
    nextAutoNumber,
    addRider,
    updateRider,
    deleteRider,
    updateSettings,
    checkFileNumberConflict,
    toggleRiderStatus,
    refresh,
  };
}
